<template>
  <PageLayout>
    <template v-slot:header>
      <div class="ui padded grid">
        <div class="row">
          <div class="eight wide column">
            <Breadcrumb>
              <a v-go-back="'users'">Usuários</a>
              <i class="right chevron icon divider"></i>
              <div class="active section">{{ sectionName }}</div>
            </Breadcrumb>
          </div>
          <div v-show="persisted" class="eight wide right aligned column">
            <div class="actions">
              <a
                class="section"
                :href="$router.resolve({ name: 'newUser' }).href"
              >
                <i class="plus icon"></i>Novo Usuário
              </a>
              <a @click.prevent="$refs.history.open">
                <i class="history icon"></i>Histórico
              </a>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-slot:main>
      <HistoryModal ref="history" resource="User" :item_id="form.id" />
      <div class="ui padded grid">
        <div v-show="loading" class="ui active inverted dimmer">
          <div class="ui text loader">Carregando...</div>
        </div>
        <div class="row">
          <div class="eight wide column">
            <form
              :class="['ui small', { error: anyError }, 'form']"
              v-autocomplete-off
            >
              <div
                :class="[{ error: errors.enable }, 'field']"
                :data-content="errorsTo('enable')"
              >
                <div class="ui toggle checkbox">
                  <input
                    type="checkbox"
                    id="user-form-enable"
                    name="enable"
                    v-model="form.enable"
                  />
                  <label for="user-form-enable">Usuário ativo?</label>
                </div>
              </div>
              <br />
              <div class="fields">
                <div
                  :class="[
                    'required four wide',
                    { error: errors.rule },
                    'field'
                  ]"
                  :data-content="errorsTo('rule')"
                >
                  <label for="user-form-rule">Tipo de Usuário</label>
                  <select id="user-form-rule" v-model="form.rule">
                    <option
                      v-for="(rule, index) in UserEnum.rules"
                      :key="index"
                      :value="rule.value"
                    >
                      {{ rule.description }}
                    </option>
                  </select>
                </div>
                <div
                  v-show="form.rule == 'collaborator'"
                  :class="[
                    'required twelve wide',
                    { error: errors.profile_id },
                    'field'
                  ]"
                  :data-content="errorsTo('profile_id')"
                >
                  <label for="user-form-profile-id">Perfil de Acesso</label>
                  <select id="user-form-profile-id" v-model="form.profile_id">
                    <option :value="null"></option>
                    <option
                      v-for="(profile, index) in profiles"
                      :key="index"
                      :value="profile.id"
                    >
                      {{ profile.description }}
                    </option>
                  </select>
                </div>
                <div
                  v-show="form.rule == 'doctor'"
                  :class="[
                    'twelve wide',
                    { error: errors.medical_specialty },
                    'field'
                  ]"
                  :data-content="errorsTo('medical_specialty')"
                >
                  <label for="user-form-medical-speciality">
                    Especialidade
                  </label>
                  <input
                    type="text"
                    autofocus
                    id="user-form-medical-speciality"
                    v-model="form.medical_specialty"
                  />
                </div>
              </div>
              <div class="fields">
                <div class="four wide field">
                  <label for="user-form-person">Tipo de Pessoa</label>
                  <select id="user-form-person" v-model="form.person">
                    <option
                      v-for="(person, index) in UserEnum.persons"
                      :key="index"
                      :value="person.value"
                    >
                      {{ person.description }}
                    </option>
                  </select>
                </div>
                <div
                  :class="[
                    'required twelve wide',
                    { error: errors.name },
                    'field'
                  ]"
                  :data-content="errorsTo('name')"
                >
                  <label for="user-form-name">Nome</label>
                  <input
                    type="text"
                    autofocus
                    id="user-form-name"
                    v-model="form.name"
                  />
                </div>
              </div>
              <div v-show="form.rule == 'partner'" class="fields">
                <div class="four wide field">
                  <label for="user-logo-image">Logo</label>
                  <ImageInput
                    @selected="id => updatePartnerLogo(id)"
                    title="Logo"
                    item="partner_logo"
                    :item_id="form.partner_logo_id"
                    sent-text="Enviar logo"
                  />
                </div>
                <div class="twelve wide field">
                  <label for="user-logo-image">Cover</label>
                  <ImageInput
                    @selected="id => updatePartnerCover(id)"
                    title="Cover"
                    item="partner_cover"
                    :item_id="form.partner_cover_id"
                    sent-text="Enviar cover"
                    :aspect-ratio="16 / 9"
                  />
                </div>
              </div>
              <div
                class="field"
                v-show="form.rule == 'partner' && $allows('partner_category')"
              >
                <label for="user-partner-categories">Categorias</label>
                <select
                  id="user-partner-categories"
                  class="ui fluid search dropdown"
                  multiple=""
                  v-model="form.partner_categories"
                >
                  <option
                    v-for="(partner_category, index) in partnerCategories"
                    :key="index"
                    :value="partner_category.id"
                  >
                    {{ partner_category.description }}
                  </option>
                </select>
              </div>
              <div
                v-show="form.rule == 'partner'"
                :class="[{ error: errors.partner_services }, 'field']"
                :data-content="errorsTo('partner_services')"
              >
                <label for="user-form-partner-services">Serviços:</label>
                <textarea
                  id="user-form-partner-services"
                  v-model="form.partner_services"
                >
                </textarea>
              </div>
              <div class="fields">
                <div
                  v-show="form.person == 'individual'"
                  :class="['four wide', { error: errors.person }, 'field']"
                  :data-content="errorsTo('person')"
                >
                  <label for="user-form-birthday">Data de Nascimento</label>
                  <input
                    type="text"
                    v-mask="'##/##/####'"
                    id="user-form-birthday"
                    v-model="form.birthday"
                  />
                </div>
                <div
                  v-show="form.person == 'individual'"
                  :class="['four wide', { error: errors.gender }, 'field']"
                  :data-content="errorsTo('gender')"
                >
                  <label for="user-form-gender">Gênero</label>
                  <select id="user-form-gender" v-model="form.gender">
                    <option
                      v-for="(gender, index) in UserEnum.genders"
                      :key="index"
                      :value="gender.value"
                    >
                      {{ gender.description }}
                    </option>
                  </select>
                </div>
              </div>
              <div
                :class="[{ error: errors.obs }, 'field']"
                :data-content="errorsTo('obs')"
              >
                <label for="user-form-obs">Observações:</label>
                <textarea id="user-form-obs" v-model="form.obs"></textarea>
              </div>
              <h4 class="ui dividing header">Documentos:</h4>
              <div v-show="form.person == 'individual'" class="fields">
                <div
                  :class="['four wide', { error: errors.cpf }, 'field']"
                  :data-content="errorsTo('cpf')"
                >
                  <label for="user-form-cpf">CPF</label>
                  <input
                    type="text"
                    v-mask="'###.###.###-##'"
                    v-model="form.cpf"
                    id="user-form-cpf"
                  />
                </div>
                <div
                  :class="['six wide', { error: errors.rg }, 'field']"
                  :data-content="errorsTo('rg')"
                >
                  <label for="user-form-rg">RG</label>
                  <input type="text" id="user-form-rg" v-model="form.rg" />
                </div>
                <div
                  :class="['six wide', { error: errors.cnh }, 'field']"
                  :data-content="errorsTo('cnh')"
                >
                  <label for="user-form-cnh">CNH</label>
                  <input type="text" id="user-form-cnh" v-model="form.cnh" />
                </div>
              </div>
              <div v-show="form.person == 'corporation'" class="fields">
                <div
                  :class="['six wide', { error: errors.cnpj }, 'field']"
                  :data-content="errorsTo('cnpj')"
                >
                  <label for="user-form-cnpj">CNPJ</label>
                  <input
                    v-mask="'##.###.###/####-##'"
                    type="text"
                    id="user-form-cnpj"
                    v-model="form.cnpj"
                  />
                </div>
                <div
                  :class="['six wide', { error: errors.ie }, 'field']"
                  :data-content="errorsTo('ie')"
                >
                  <label for="user-form-rg">Inscrição Estadual</label>
                  <input type="text" id="user-form-ie" v-model="form.rg" />
                </div>
              </div>
              <h4 class="ui dividing header">Endereço:</h4>
              <div
                :class="['four wide', { error: errors.cep }, 'field']"
                :data-content="errorsTo('cep')"
              >
                <label for="user-form-cep">CEP</label>
                <input
                  type="text"
                  v-mask="'##.###-###'"
                  v-model="form.cep"
                  id="user-form-cep"
                  @keyup.enter="fetchCep"
                />
              </div>
              <div
                :class="[{ error: errors.city_id }, 'field']"
                :data-content="errorsTo('city_id')"
              >
                <label for="user-form-city">Cidade</label>
                <CityInput
                  @selected="cityData => updateCityData(cityData)"
                  ref="city"
                ></CityInput>
              </div>
              <div class="fields">
                <div
                  :class="[{ error: errors.address }, 'fourteen wide field']"
                  :data-content="errorsTo('address')"
                >
                  <label for="user-form-address">Logradouro</label>
                  <input
                    type="text"
                    id="user-form-address"
                    v-model="form.address"
                  />
                </div>
                <div
                  :class="[{ error: errors.address_number }, 'two wide field']"
                  :data-content="errorsTo('address-number')"
                >
                  <label for="user-form-address-number">Número</label>
                  <input
                    type="text"
                    id="user-form-address-number"
                    v-model="form.address_number"
                  />
                </div>
              </div>
              <div class="fields">
                <div
                  :class="['eight wide', { error: errors.complement }, 'field']"
                  :data-content="errorsTo('complement')"
                >
                  <label for="user-form-complement">Complemento</label>
                  <input
                    type="text"
                    id="user-form-complement"
                    v-model="form.complement"
                  />
                </div>
                <div
                  :class="[
                    'eight wide',
                    { error: errors.neighborhood },
                    'field'
                  ]"
                  :data-content="errorsTo('neighborhood')"
                >
                  <label for="user-form-neighborhood">Bairro</label>
                  <input
                    type="text"
                    id="user-form-neighborhood"
                    v-model="form.neighborhood"
                  />
                </div>
              </div>
              <h4 class="ui dividing header">Contato Principal</h4>
              <div class="ui two fields">
                <div
                  :class="[{ error: errors.email }, 'field']"
                  :data-content="errorsTo('email')"
                >
                  <label for="user-form-email">E-mail</label>
                  <input
                    type="email"
                    id="user-form-email"
                    v-model="form.email"
                  />
                </div>
                <div
                  :class="[{ error: errors.cellphone }, 'field']"
                  :data-content="errorsTo('cellphone')"
                >
                  <label for="user-form-cellphone">Número de celular</label>
                  <input
                    type="text"
                    id="user-form-cellphone"
                    mask="(00)00000-0000"
                    v-mask="'(##)#####-####'"
                    v-model="form.cellphone"
                  />
                </div>
              </div>
              <div v-if="$allows('contacts')">
                <h4 class="ui dividing header">Outros contatos</h4>
                <div class="ui message">
                  <p>Números de contatos que serão exibidos no aplicativo.</p>
                </div>
                <div v-if="anyContactsError" class="ui error message">
                  <ul class="list">
                    <li
                      v-for="(msg, errorContactIndex) in errors.contacts"
                      :key="errorContactIndex"
                    >
                      {{ msg }}
                    </li>
                  </ul>
                </div>
                <div
                  v-for="(contact, index) in contacts"
                  :key="index"
                  class="ui fields"
                >
                  <div class="four wide required field">
                    <label for="contact-kind">Tipo</label>
                    <select id="contact-kind" v-model="contact.kind">
                      <option
                        v-for="(rule, index) in UserEnum.contacts"
                        :key="index"
                        :value="rule.value"
                      >
                        {{ rule.description }}
                      </option>
                    </select>
                  </div>
                  <div class="six wide field">
                    <label for="contact-description">Descrição</label>
                    <input type="text" v-model="contact.description" />
                  </div>
                  <div class="six wide end required field">
                    <label for="contact-info">Número</label>
                    <input
                      type="text"
                      v-model="contact.info"
                      mask="(00)00000-0000"
                      v-mask="'(##)#####-####'"
                    />
                  </div>
                  <div class="remove field">
                    <a
                      class="delete"
                      title="Remover"
                      @click.prevent="removeContact(index)"
                      ><i class="trash icon"></i
                    ></a>
                  </div>
                </div>
                <a @click.prevent="addContact">
                  <i class="plus icon"></i> Adicionar Contato
                </a>
              </div>
              <h4 class="ui dividing header">Autenticação</h4>
              <div
                :class="[{ error: errors.username }, 'field']"
                :data-content="errorsTo('username')"
              >
                <label for="user-form-login">Login</label>
                <input
                  type="text"
                  id="user-form-login"
                  v-model="form.username"
                />
              </div>
              <div class="fields">
                <div
                  :class="['eight wide', { error: errors.password }, 'field']"
                  :data-content="errorsTo('password')"
                >
                  <label for="user-form-password">Senha</label>
                  <input
                    type="password"
                    name="password"
                    id="user-form-password"
                    v-model="form.password"
                  />
                </div>
                <div
                  :class="[
                    'eight wide',
                    { error: errors.password_confirmation },
                    'field'
                  ]"
                  :data-content="errorsTo('password_confirmation')"
                >
                  <label for="user-form-password-confirmation"
                    >Confirmar Senha</label
                  >
                  <input
                    type="password"
                    name="password_confirmation"
                    id="user-form-password-confirmation"
                    v-model="form.password_confirmation"
                  />
                </div>
              </div>

              <div class="ui divider"></div>
              <div class="field">
                <button
                  @click="save"
                  type="button"
                  name="save"
                  class="ui submit blue button"
                >
                  <i class="save icon"></i>Salvar
                </button>
                <a v-show="persisted" class="delete" @click.prevent="remove">
                  <i class="trash icon"></i>
                  Remover Usuário
                </a>
              </div>
            </form>
          </div>
        </div>
      </div>
    </template>
  </PageLayout>
</template>

<script>
import Api from '@/resources/Api';
import CepResource from '@/resources/Cep';
import Breadcrumb from '@/components/breadcrumb';
import CityInput from '@/components/city_input';
import ImageInput from './widgets/image_input';
import HistoryModal from '@/components/history_modal';
import PageLayout from '@/layouts/page_layout';
import UserEnum from '@/enumerations/user_enum';

export default {
  name: 'users',
  components: { Breadcrumb, ImageInput, PageLayout, CityInput, HistoryModal },

  data() {
    return {
      UserEnum: UserEnum,
      loading: false,
      loadingCep: false,
      errors: {},
      profiles: [],
      partnerCategories: [],
      form: {
        contacts: [],
        id: null,
        name: null,
        birthday: null,
        gender: null,
        partner_services: null,
        person: 'individual',
        cpf: null,
        rg: null,
        cnh: null,
        cnpj: null,
        ie: null,
        cep: null,
        address: null,
        address_number: null,
        neighborhood: null,
        complement: null,
        permissions: [],
        partner_categories: [],
        obs: null,
        email: null,
        username: null,
        enable: true,
        password_confirmation: null,
        password: null,
        city_ibge_code: null,
        rule: 'client',
        profile_id: null,
        partner_logo_id: null,
        partner_cover_id: null,
        medical_specialty: null
      }
    };
  },

  computed: {
    persisted() {
      return this.form.id != null;
    },

    anyContactsError() {
      return (this.errors.contacts || []).length;
    },

    contacts() {
      return this.form.contacts.filter(b => !b._destroy);
    },

    sectionName() {
      return this.persisted
        ? `Editar Usuário - ${this.form.id}`
        : 'Novo Usuário';
    },

    anyError() {
      return !_.isEmpty(this.errors);
    }
  },

  mounted() {
    this.init();
  },

  methods: {
    async init() {
      await this.getProfiles();
      await this.getParterCategories();

      if (this.$route.params.id) {
        this.fetch(this.$route.params.id);
      }
    },

    async addContact() {
      this.form.contacts.push({
        id: null,
        kind: null,
        info: null,
        description: null,
        _destroy: false
      });
      await this.$sleep(10);
      // $(this.$el).find('.member-user-id-input:last').focus();
    },

    removeContact(index) {
      const contact = this.contacts[index];
      if (contact.id != null) {
        contact._destroy = true;
        this.$set(this.form.contacts, index, contact);
      } else {
        this.$delete(this.form.contacts, index);
      }
    },

    errorsTo(key) {
      const errors = _.isEmpty(this.errors[key]) ? [] : this.errors[key];
      return errors.join(', ');
    },

    _setErrors(errors) {
      this.errors = _.clone(errors);
      setTimeout(() => {
        $('.error.field').popup();
      }, 100);
    },

    setForm(data) {
      data.birthday = this.$moment(data.birthday).format('DD/MM/YYYY');
      this.form = _.clone(data);

      if (!_.isEmpty(data.city)) {
        this.$refs.city.setCity(data.city.name, data.city.ibge_code);
        this.form.city_ibge_code = data.city.ibge_code;
      }

      this.form.partner_categories.forEach(category => {
        $('#user-partner-categories').dropdown('set selected', category);
      });

      // $('#user-partner-categories').dropdown(
      //   'set selected',
      //   this.form.partner_categories
      // );
    },

    updatePartnerCover(imageId) {
      this.form.partner_cover_id = imageId;

      const data = {
        id: this.form.id,
        partner_cover_id: imageId
      };

      Api.users.save(data);
    },

    updatePartnerLogo(imageId) {
      this.form.partner_logo_id = imageId;

      const data = {
        id: this.form.id,
        partner_logo_id: imageId
      };

      Api.users.save(data);
    },

    save() {
      this.loading = true;
      this._setErrors({});

      const data = { ...this.form };
      data.cep = this.$raw(data.cep);
      data.cpf = this.$raw(data.cpf);
      data.birthday = data.birthday
        ? this.$moment(data.birthday, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : null;
      data.cellphone = this.$raw(data.cellphone);

      data.contacts_attributes = data.contacts;
      data.contacts_attributes.forEach(contact => {
        contact.info = this.$raw(contact.info);
      });

      Api.users
        .save(data)
        .then(async response => {
          this.$toast.show('Usuário salvo com sucesso', 'success');
          this.setForm(response.data.user);
        })
        .catch(error => {
          if (error.response) {
            const errors = error.response.data.errors;
            const baseErrors = errors && errors.base;
            if (baseErrors && baseErrors.length) {
              this.$toast.show(baseErrors.join(', '), 'error');
            } else {
              this.$toast.show('Erro ao salvar usuário', 'error');
            }
            if (error.response.status != 500) {
              this._setErrors(errors);
            }
          } else {
            throw error;
          }
        })
        .finally(() => {
          this.$scrollTop();
          this.loading = false;
        });
    },

    remove() {
      const removeIt = confirm('Remover Usuário?');
      if (removeIt) {
        this.loading = true;
        Api.users
          .destroy(this.form.id)
          .then(async () => {
            this.$toast.show('Usuário removido com sucesso', 'success');
            this.$router.push({ name: 'users' });
          })
          .catch(error => {
            if (error.response) {
              const errors = error.response.data.errors;
              const baseErrors = errors && errors.base;
              if (baseErrors && baseErrors.length) {
                this.$toast.show(baseErrors.join(', '), 'error');
              } else {
                this.$toast.show('Erro ao remover usuário', 'error');
              }
              this._setErrors(errors);
            } else {
              throw error;
            }
          })
          .finally(() => {
            this.$scrollTop();
            this.loading = false;
          });
      }
    },

    fetch(id) {
      this.loading = true;
      Api.users
        .fetch(id)
        .then(async response => {
          console.log('fetch');
          this.setForm(response.data.user);
        })
        .catch(error => {
          if (error.response) {
            this.$toast.show('Erro ao buscar usuário', 'error');
            this._setErrors(error.response.data.errors);
            this.loading = false;
          } else {
            throw error;
          }
        })
        .finally(() => {
          this.$scrollTop();
          this.loading = false;
        });
    },

    updateCityData(cityData) {
      this.form.city_ibge_code = cityData.value;
    },

    getParterCategories() {
      this.loading = true;
      return Api.partnerCategories
        .search({ all: true })
        .then(async response => {
          this.partnerCategories = _.sortBy(
            response.data.partner_categories,
            'description'
          );

          $('#user-partner-categories').dropdown();
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getProfiles() {
      this.loading = true;
      return Api.profiles
        .search({})
        .then(async response => {
          console.log('getProfiles');
          this.profiles = _.sortBy(response.data.profiles, 'description');
        })
        .finally(() => {
          this.loading = false;
        });
    },

    fetchCep() {
      this.loadingCep = true;
      const cep = this.$raw(this.form.cep);
      CepResource.fetch(cep)
        .then(response => {
          const cityData = response.data;
          this.form.address = cityData.logradouro;
          this.form.neighborhood = cityData.bairro;

          this.$refs.city.setCity(
            cityData.cidade,
            cityData.cidade_info.codigo_ibge
          );

          this.form.city_ibge_code = cityData.cidade_info.codigo_ibge;
        })
        .catch(error => {
          this.$toast.show('Erro ao buscar CEP', 'error');
          throw error;
        })
        .finally(() => {
          this.loadingCep = false;
        });
    }
  }
};
</script>
